import { Moon, Play, Sun } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { useAuthActions } from '@creditornot/wolt-auth/react';
import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ConfirmModal } from 'common/components';
import Send from 'common/components/icons/Send';
import useUser from 'common/hooks/useUser';
import { useThemeMode } from 'common/themeMode';
import { PWAContext } from 'core/pwa/PWAContext';
import { telemetryClickInstallFromUserDropDown, telemetryClickIOSInstructions } from 'core/pwa/telemetry';
import { UiLanguageKey } from 'locale/languages';
import { ExperienceNps } from 'modules/nps/features/ExperienceNps';
import useIsNpsFlagEnabled from 'modules/nps/useIsNpsFlagEnabled';
import { useHandleSupport } from 'modules/support';

import LanguageSelectorMenuItemGroup from './LanguageSelectorMenuItemGroup';
import { useMe } from '../../hooks/useMe';
import { useToggleNewExperience } from '../../hooks/useToggleNewExperience';

const UserDropDownContainer = styled.div`
  overflow-y: auto;
`;

const MenuRoot = styled.div`
  max-height: calc(100vh - 100px);
`;

const StyledMenuItemGroup = styled(MenuItemGroup)`
  padding: 12px 16px;
  &:not(:first-child) {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const StyledLanguageSelectorMenuItem = styled(LanguageSelectorMenuItemGroup)`
  padding: 12px 16px;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const UserTitle = styled.h3`
  font-size: 15px;
  font-weight: 500;
  color: ${cbSemanticCssVars.textPrimary};
  margin-block-end: 0;
`;

const SendIcon = styled(Send)`
  fill: ${cbSemanticCssVars.textPrimary};
`;

const useLogout = (): [boolean, () => void] => {
  const { logout } = useAuthActions();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const doLogout = () => {
    setIsLoggingOut(true);
    logout();
  };

  return [isLoggingOut, doLogout];
};

/**
 * Do not use experience module context directly here as it's also being used in /experience/search
 */
export const UserDropDown = ({
  hideDropDown,
  onChangeLanguage,
  closeSidebar,
}: {
  hideDropDown: () => void;
  onChangeLanguage?: (language: UiLanguageKey) => void;
  closeSidebar?: () => void;
}) => {
  const { themeMode, setThemeMode } = useThemeMode();
  const { shouldShowInstallGeneralPrompt, shouldShowInstallIOSPrompt, setIsInstructionsModalOpen, install } =
    useContext(PWAContext);

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isLoggingOut, doLogout] = useLogout();

  const { data: user } = useMe();
  const { isEmployee } = useUser();
  const { toggleExperience } = useToggleNewExperience();
  const [showNps, setShowNps] = useState(false);
  const isNpsFlagEnabled = useIsNpsFlagEnabled();

  const handleShowInstallPrompt = () => {
    if (shouldShowInstallIOSPrompt) {
      telemetryClickIOSInstructions();
      setIsInstructionsModalOpen(true);
    }

    if (shouldShowInstallGeneralPrompt) {
      telemetryClickInstallFromUserDropDown();
      install();
    }
  };

  const handleSupport = useHandleSupport({
    onSupportLayerEnabled: () => {
      hideDropDown();
      closeSidebar?.();
    },
  });

  return (
    <UserDropDownContainer>
      <MenuRoot>
        <StyledMenuItemGroup>
          <UserTitle>{user?.email || ''}</UserTitle>
        </StyledMenuItemGroup>
        <StyledLanguageSelectorMenuItem onChange={onChangeLanguage} />
        <StyledMenuItemGroup>
          <MenuActionItem
            variant="black"
            icon={themeMode === 'light' ? <Moon /> : <Sun />}
            onClick={() => setThemeMode(themeMode === 'dark' ? 'light' : 'dark')}
          >
            <FormattedMessage id={themeMode === 'light' ? 'navigation.switch-dark' : 'navigation.switch-light'} />
          </MenuActionItem>
          {isNpsFlagEnabled && (
            <MenuActionItem variant="black" icon={<SendIcon />} onClick={() => setShowNps(true)}>
              <FormattedMessage id="navigation.give-feedback" />
            </MenuActionItem>
          )}
          {shouldShowInstallGeneralPrompt ||
            (shouldShowInstallIOSPrompt && (
              <MenuActionItem variant="black" icon={<Play />} onClick={handleShowInstallPrompt}>
                <FormattedMessage id="navigation.install-app" />
              </MenuActionItem>
            ))}
          {/* TODO: @navjotahuja92 Only for employee. To be removed */}
          {isEmployee && (
            <MenuActionItem
              variant="black"
              onClick={() => {
                toggleExperience();
              }}
            >
              Toggle Experience
            </MenuActionItem>
          )}
          <MenuActionItem
            variant="black"
            onClick={() => {
              handleSupport();
            }}
          >
            <FormattedMessage id="navigation.support" />
          </MenuActionItem>
          <MenuActionItem variant="black" onClick={() => setIsLogoutModalOpen(true)}>
            <FormattedMessage id="navigation.logout" />
          </MenuActionItem>
        </StyledMenuItemGroup>
      </MenuRoot>

      <ConfirmModal
        header={<FormattedMessage id="logout.header" />}
        description={<FormattedMessage id="logout.description" values={{ user: user?.email }} />}
        cancelText={<FormattedMessage id="logout.cancel" />}
        confirmText={<FormattedMessage id="logout.sign-out" />}
        onCancel={() => setIsLogoutModalOpen(false)}
        onConfirm={doLogout}
        open={isLogoutModalOpen}
        loading={isLoggingOut}
      />
      {showNps && <ExperienceNps manual onClose={() => setShowNps(false)} />}
    </UserDropDownContainer>
  );
};
